/**
 * @module app.core
 */

import { autoinject, bindingMode, bindable, TaskQueue } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { ProviderService } from 'app/cd/services/provider-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssTableCustomElement, SelectEvent } from 'app/shared/elements/ess-table';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import * as CD from 'app/cd/models/essette-api';

interface CustomProvider extends CD.MemberProvider {
    displayName?: string // to display both full name and id
}
@autoinject
export class CorrespondenceAssociatedProvidersGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected memberId!: string;
    @bindable({ defaultBindingMode: bindingMode.fromView })
    protected selectedProviders: Array<CustomProvider> = [];

    protected providers: ReadonlyArray<CustomProvider> = [];

    protected sorting = new Sorting();
    protected defaultSorting = new Sorting();
    protected includeNonCurrent: boolean = false;

    protected resultCount: number = 0;
    protected pageSize: number = 10;

    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<CustomProvider>;
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly providerService: ProviderService,
        private readonly queue: TaskQueue,
        ...rest: any[]) {

        super(...rest);
        this.defaultSorting.entries = [
            {
                reference: 'provider.name.last',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        if(!this.memberId) { throw new Error('Bindable property [member-id] was not set'); }
        this.reload();
    }

    protected detached(): void {
        this.selectedProviders.splice(0);
    }


    ////****************** Events ***************************************************////
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    protected providersSelected({ detail }: SelectEvent<CustomProvider>): void {
        // if item is missing - a select all event occurred
        if(!detail.item) {
            // unselect all items
            if(detail.items.length === 0) {
                this.providers.forEach(item => {
                    const index = this.selectedProviders.findIndex(
                        selected => selected.id === item.id
                    );
                    if(index !== -1) {
                        this.selectedProviders.splice(index, 1);
                    }
                });
            } else {
                // save all items
                detail.items.forEach(item => {
                    const alreadySelected = this.selectedProviders.some(
                        service => service.id === item.id
                    );
                    if(!alreadySelected) {
                        this.selectedProviders.push(item);
                    }
                });
            }
        } else {
            // save selected checkboxes to restore later logic
            const match = detail.items.some(
                item => item.id === detail.item?.id
            );
            if(match) {
                // add to selectedProviders if not already present
                const alreadySelected = this.selectedProviders.some(
                    service => service.id === detail.item?.id
                );
                if(!alreadySelected) {
                    this.selectedProviders.push(detail.item!);
                }
            } else {
                // remove from selectedProviders if it exists based on the id of detail
                const index = this.selectedProviders.findIndex(
                    service => service.id === detail.item?.id
                );
                if(index !== -1) {
                    this.selectedProviders.splice(index, 1);
                }
            }
        }

    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadAssociatedProviders();
    }

    private restoreSelected() {
        if(!(this.selectedProviders && this.selectedProviders.length > 0)) { return; }
        // Give aurelia a chance to bind data to the table before we ask the table to restore selected items
        this.queue.queueTask(() => {
            this.$table.restoreSelected(this.selectedProviders);
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAssociatedProviders(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting.entries.length ? this.sorting : this.defaultSorting
        };

        const includes = [
            'provider',
            'provider.location',
            'relationship'
        ];

        const fields = [
            'dateRange.end',
            'dateRange.start',
            'provider.name.full',
            'relationship.description',
        ];

        try {
            this.state.beginLoad();

            const response = await this.providerService.loadMemberProvidersByMember(this.memberId, options, includes, fields, this.includeNonCurrent);
            this.providers = response.results;
            this.providers.forEach(p => {
                p.displayName = `${p.provider?.name?.full} (${p.provider?.id})`;
            });

            this.resultCount = response.count || 0;

            this.restoreSelected();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
